import { AfterViewChecked, Component, ElementRef, EventEmitter, Input, OnChanges, Output, SimpleChanges, ViewChild } from '@angular/core';
import { finalize } from 'rxjs';
import { ReportsService } from 'src/app/reports/reports.service';
import templateCatalog from './templates.json';

interface AskPromptTemplate {
  elementType: string;
  template: string;
  example: string;
}

const promptTemplates: AskPromptTemplate[] = templateCatalog.templates;

/**
 * Generic "describe what you want in plain language" prompt panel.
 * Presentational only - reusable anywhere an ASK-style tab is needed (filters, visuals, etc.).
 */
@Component({
  selector: 'app-ask-panel',
  standalone: false,
  templateUrl: './ask-panel.component.html',
  styleUrls: ['./ask-panel.component.scss']
})
export class AskPanelComponent implements AfterViewChecked, OnChanges {
  @ViewChild('conversationThread') conversationThread?: ElementRef<HTMLDivElement>;
  conversationHistory: { role: 'user' | 'response'; text: string; candidateTables?: any[]; canProceed?: boolean }[] = [];
  el: any;
  generating = false;
  constructor(private reportsService: ReportsService) { }
  @Input() icon = 'auto_awesome';
  @Input() title = 'What would you like to add?';
  @Input() description = 'Use plain language. You can review the result in Visual Editor.';
  @Input() placeholder = 'Describe what you need';
  @Input() helperText = '';
  @Input() maxLength = 500;
  @Input() disabled = false;
  @Input() value = '';
  @Input() notes: string[] = [];
  @Input() feedbackMessage = '';
  @Input() reportId: string | number | null = null;
  @Output() valueChange = new EventEmitter<string>();
  @Output() generated = new EventEmitter<any>();
  @Output() proceedToVisualEditor = new EventEmitter<void>();
  candidateTables: any[] = [];
  sqlValidation: any = null;
  hideconversationbox = false;
  sessionId: string = '';
  @Input() selectedElementType = '';
  private requestGeneration = 0;
  private startNewSession = true;
  private lastRenderedMessage?: { role: 'user' | 'response'; text: string; candidateTables?: any[] };

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['selectedElementType']) {
      this.applyTemplate();
    }
  }

  ngAfterViewChecked(): void {
    const latestMessage = this.conversationHistory[this.conversationHistory.length - 1];
    if (latestMessage && latestMessage !== this.lastRenderedMessage && this.conversationThread) {
      const thread = this.conversationThread.nativeElement;
      thread.scrollTop = thread.scrollHeight;
    }
    this.lastRenderedMessage = latestMessage;
  }
  get hasValidPrompt(): boolean {
    return this.value.trim().length > 0;
  }

  get hasReportId(): boolean {
    return this.reportId !== null && this.reportId !== undefined && String(this.reportId).trim().length > 0;
  }

  get selectedTemplate(): AskPromptTemplate | undefined {
    return promptTemplates.find(template => template.elementType === this.selectedElementType);
  }

  private applyTemplate(): void {
    const template = this.selectedTemplate;
    if (template) {
      this.onInput(template.template);
    }
  }

  onInput(text: string): void {
    this.value = text;
    this.valueChange.emit(this.value);
  }

  onKeydown(event: KeyboardEvent): void {
    if (event.key !== 'Enter' || event.shiftKey) { return; }
    event.preventDefault();
    this.generateReport();
  }

  onEnter(text: string): void {
    this.onInput(text);
    this.generateReport();
  }

  resetState(): void {
    this.requestGeneration++;
    this.startNewSession = true;
    this.sessionId = '';
    this.generating = false;
    this.value = this.selectedTemplate?.template || '';
    this.valueChange.emit(this.value);
    this.el = null;
    this.conversationHistory = [];
    this.candidateTables = [];
    this.sqlValidation = null;
    this.hideconversationbox = false;
  }

  generateReport(): void {
    if (this.disabled || this.generating || !this.hasValidPrompt || !this.hasReportId) { return; }

    this.generating = true;
    this.sqlValidation = null;
    const prompt = this.value.trim();
    this.conversationHistory.push({ role: 'user', text: prompt });
    const requestGeneration = ++this.requestGeneration;
    const payload: any = {
      reportId: this.reportId,
      message: prompt,
      startNewSession: this.startNewSession,
    };
    if (this.selectedElementType) {
      //payload.elementType = this.selectedElementType;
    }
    if (!this.startNewSession && this.sessionId) {
      payload.sessionId = this.sessionId;
    }
    this.reportsService.generateAiReportJson(payload).pipe(
      finalize(() => {
        if (requestGeneration === this.requestGeneration) {
          this.generating = false;
        }
      })
    ).subscribe(
      response => {
        if (requestGeneration !== this.requestGeneration) { return; }
        // Response shape varies a lot by elementType/flow (filters go through SQL validation,
        // other visuals may return elementJson/generatedSql directly) - log it to make that visible.
        console.log('ASK API response:', response);

        const responseSessionId = String(response?.sessionId || '');
        if (responseSessionId) {
          this.startNewSession = false;
        }
        if (responseSessionId && this.sessionId && this.sessionId !== responseSessionId) {
          this.el = null;
          this.conversationHistory = [];
          this.candidateTables = [];
          this.sqlValidation = null;
          this.hideconversationbox = false;
          this.conversationHistory.push({ role: 'user', text: prompt });
        }
        if (responseSessionId) {
          this.sessionId = responseSessionId;
        }
        this.selectedElementType = response?.elementType;
        this.el = { element_json: response?.elementJson };
        this.candidateTables = response?.candidateTables || [];
        this.sqlValidation = response?.validationStatus || null;

        if (response?.question) {
          this.conversationHistory.push({ role: 'response', text: response.question, candidateTables: this.candidateTables });
        } else if (this.sqlValidation === 'PASS') {
          this.conversationHistory.push({ role: 'response', text: 'SQL validation passed.', canProceed: true });
        }

        if (this.sqlValidation === 'PASS') {
          this.hideconversationbox = true;
          this.generated.emit(response);
        }

      },
      error => {
        if (requestGeneration !== this.requestGeneration) { return; }
        this.conversationHistory.push({ role: 'response', text: 'Something went wrong. Please try again.' });
        console.error('HTTP error:', error);
      }
    );
  }
}
